// src/components/AttorneyRecords.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const AttorneyRecords = ({ authAxios, users, userRole, showFormMessage, navigateTo, setNavigateTo }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({
    practicing_certificate: null,
    id_document: null,
    cv: null,
    law_society_receipt: null,
  });

  const canManageAll = useMemo(
    () => ['PARTNER', 'SUPPORT_STAFF', 'SUPERUSER'].includes(userRole),
    [userRole]
  );

  const canView = useMemo(
    () => ['PARTNER', 'ASSOCIATE', 'SUPPORT_STAFF', 'SUPERUSER'].includes(userRole),
    [userRole]
  );

  const attorneyUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list.filter(u => ['PARTNER', 'ASSOCIATE'].includes(u.role));
  }, [users]);

  const fetchProfiles = useCallback(async () => {
    if (!canView) {
      setProfiles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      if (canManageAll) {
        const resp = await authAxios.get('staff-profiles/');
        const data = resp.data?.results || resp.data || [];
        const attorneyProfiles = (Array.isArray(data) ? data : []).filter(
          p => ['PARTNER', 'ASSOCIATE'].includes(p.role)
        );
        setProfiles(attorneyProfiles);
        if (attorneyProfiles.length > 0) {
          setSelectedProfileId(prev => (prev ? prev : attorneyProfiles[0].id));
        }
      } else {
        const resp = await authAxios.get('staff-profiles/me/');
        const me = resp.data;
        setProfiles(me ? [me] : []);
        setSelectedProfileId(me?.id || null);
      }
    } catch (err) {
      console.error('Failed to fetch attorney profiles', err);
      showFormMessage('danger', 'Failed to load attorney records.');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [authAxios, canManageAll, canView, showFormMessage]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'attorneyRecords' && navigateTo.id) {
      setSelectedProfileId(navigateTo.id);
      if (setNavigateTo) setNavigateTo(null);
    }
  }, [navigateTo, setNavigateTo]);

  const selectedProfile = useMemo(
    () => profiles.find(p => p.id === selectedProfileId) || profiles[0] || null,
    [profiles, selectedProfileId]
  );

  const resetPendingFiles = useCallback(() => {
    setPendingFiles({
      practicing_certificate: null,
      id_document: null,
      cv: null,
      law_society_receipt: null,
    });
  }, []);

  const handleFileChange = useCallback((field, file) => {
    setPendingFiles(prev => ({ ...prev, [field]: file || null }));
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedProfile) return;

    const formData = new FormData();
    Object.entries(pendingFiles).forEach(([k, v]) => {
      if (v) formData.append(k, v);
    });

    if ([...formData.keys()].length === 0) {
      showFormMessage('warning', 'Select at least one file to upload.');
      return;
    }

    try {
      await authAxios.patch(`staff-profiles/${selectedProfile.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showFormMessage('success', 'Attorney documents uploaded successfully.');
      resetPendingFiles();
      fetchProfiles();
    } catch (err) {
      console.error('Upload failed', err?.response?.data || err);
      showFormMessage('danger', 'Upload failed. Please try again.');
    }
  }, [authAxios, fetchProfiles, pendingFiles, resetPendingFiles, selectedProfile, showFormMessage]);

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600">Access Denied</h2>
          <p className="text-gray-600 mt-2">You do not have permission to view attorney records.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Attorney / Practitioner Records</h1>
          <p className="text-gray-600 mt-1">Upload practicing certificate, ID, CV and law society receipt to remain compliant.</p>
        </div>
      </div>

      {loading ? (
        <div className="bg-white shadow rounded-lg p-6">Loading…</div>
      ) : (
        <>
          {canManageAll && profiles.length > 1 && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Attorney</label>
              <select
                className="w-full max-w-xl px-3 py-2 border border-gray-300 rounded-md"
                value={selectedProfileId || ''}
                onChange={(e) => setSelectedProfileId(Number(e.target.value))}
              >
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.username} ({p.role})
                  </option>
                ))}
              </select>

              {attorneyUsers.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">Tip: attorney users are those with role Partner/Associate.</p>
              )}
            </div>
          )}

          {!selectedProfile ? (
            <div className="bg-white shadow rounded-lg p-6">No attorney profile found.</div>
          ) : (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedProfile.first_name || selectedProfile.username} {selectedProfile.last_name || ''}
                  </div>
                  <div className="text-sm text-gray-600">{selectedProfile.email || 'No email'} · {selectedProfile.role}</div>
                </div>
                <div>
                  {selectedProfile.is_attorney_compliant ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">Compliant</span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800">Non-compliant</span>
                  )}
                </div>
              </div>

              {!selectedProfile.is_attorney_compliant && (
                <div className="mt-4 p-4 rounded-md bg-yellow-50 border border-yellow-200">
                  <div className="font-medium text-yellow-800">Missing documents</div>
                  <div className="text-sm text-yellow-800 mt-1">
                    {(selectedProfile.attorney_missing_documents || []).join(', ') || '—'}
                  </div>
                </div>
              )}

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { key: 'practicing_certificate', label: 'Practicing Certificate' },
                  { key: 'id_document', label: 'ID Document' },
                  { key: 'cv', label: 'CV' },
                  { key: 'law_society_receipt', label: 'Law Society Receipt' },
                ].map(item => (
                  <div key={item.key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium text-gray-900">{item.label}</div>
                      {selectedProfile[item.key] ? (
                        <a
                          className="text-sm text-blue-600 hover:underline"
                          href={selectedProfile[item.key]}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-sm text-gray-500">Missing</span>
                      )}
                    </div>
                    <div className="mt-3">
                      <input
                        type="file"
                        className="block w-full text-sm text-gray-700"
                        onChange={(e) => handleFileChange(item.key, e.target.files?.[0])}
                      />
                      <p className="text-xs text-gray-500 mt-1">PDF/DOC/Images supported.</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleUpload}
                >
                  Upload selected files
                </button>
                <button
                  className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                  onClick={resetPendingFiles}
                >
                  Clear
                </button>
              </div>

              {canManageAll && profiles.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Firm Overview (Attorneys)</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left border-b">
                          <th className="py-2 pr-4">User</th>
                          <th className="py-2 pr-4">Role</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Missing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profiles.map(p => (
                          <tr key={p.id} className="border-b">
                            <td className="py-2 pr-4">{p.username}</td>
                            <td className="py-2 pr-4">{p.role}</td>
                            <td className="py-2 pr-4">
                              {p.is_attorney_compliant ? (
                                <span className="text-green-700">Compliant</span>
                              ) : (
                                <span className="text-red-700">Non-compliant</span>
                              )}
                            </td>
                            <td className="py-2 pr-4">{(p.attorney_missing_documents || []).join(', ') || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AttorneyRecords;
